/**
@format
@flow
*/
import React from 'react';
import styled from 'styled-components';
import { Heading } from '@ziynx/components/typography';
import { withAppManager } from '@ziynx/components/utilities';
import Colors from '@ziynx/theme/colors';
import type { HeadingType } from '@ziynx/flow-types/globals';
import type { InlineLink } from '@ziynx/flow-types/links';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import Link from '@ziynx/components/links';
import CardBase from './CardBase';

export type Props = $ReadOnly<{|
  /**
   * Content passed into CardList
   */
  data?: Array<Object>,
  /**
   * Flex-direction for CardList.
   */
  flexDirection?: 'row' | 'column',
  /**
   * Function to render item passed into CardList.
   */
  renderItem?: (any, number) => any,
  /**
   * Function to render empty conten.
   */
  renderEmpty?: () => any,
  /**
   * key extractor
   */
  keyExtractor?: (Object, number) => string | number,
  /**
   * If set to true, card width will not respond to breakpoint and remain fixed.
   */
  fixedWidth?: boolean,
  /**
   * If fixed is true, size is set. Defaults to extraSmall
   */
  size?: 'extraSmall' | 'small' | 'medium' | 'large',
  /**
   * headline/Heading props
   */
  heading?: HeadingType,
  /**
   * Object containing InlineLink props.
   */
  link?: InlineLink,
  /**
   * Determines if Card will have 24px padding (see CardBase)
   */
  padding?: boolean,

|}>;

const Headline = styled(Heading)`
  padding-bottom: ${({ paddingBottom }) => paddingBottom};
`;

const HeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${({ width }) => width};
  max-width: 688px;
`;

const ListCard = styled(CardBase)`
  display: flex;
  justify-content: ${({ isEmpty, flexDirection }) => ((isEmpty || flexDirection === 'row') && 'center')} ;
  align-items: ${({ isEmpty }) => (isEmpty && 'center')} ;
  flex-direction: ${({ flexDirection }) => flexDirection};
  box-sizing: border-box;
  max-width: 688px;
`;

const ChildWrapper = styled.div`
  display: flex;
`;

const CardList = (props: ManagedProps<Props>): React$Node => {
  const {
    fixedWidth,
    size,
    heading,
    link,
    data,
    padding,
    keyExtractor,
    renderItem,
    renderEmpty,
    flexDirection,
    viewport,
    ...rest
  } = props;

  const { headingText, ...restHeading } = heading || {};

  const isDesktop = viewport === 'desktop' || viewport === 'desktopSmall';

  const {
    children, color, href, onClick,
  } = link || {};

  function _getFlexDirection() {
    if (flexDirection) return flexDirection;
    if (isDesktop) {
      return 'row';
    }
    return 'column';
  }

  const FLEX_DIRECTION = _getFlexDirection();

  const _getWidth = () => {
    if (!fixedWidth) return '100%';
    if (size === 'large') {
      return '688px';
    }
    if (size === 'medium') {
      return '520px';
    }
    if (size === 'small') {
      return '382px';
    }
    return '288px';
  };

  const FIXED_WIDTH = _getWidth();

  return (
    <>
      <HeadingWrapper width={FIXED_WIDTH}>
        {heading && headingText && (
          <Headline {...restHeading}>{headingText}</Headline>
        )}
        {link && children && (
          <Link large color={color} href={href} onClick={onClick} variant="text">{children}</Link>
        )}
      </HeadingWrapper>
      <ListCard
        flexDirection={FLEX_DIRECTION}
        fixedWidth={fixedWidth}
        width={FIXED_WIDTH}
        height={!data?.length ? '268px' : 'auto'}
        isEmpty={!data?.length}
        padding={padding}
        {...rest}
      >
        {(!data || !data.length)
          ? (
            <>
              {renderEmpty && renderEmpty()}
            </>
          )
          : (
            <>
              {data?.map((item, index) => {
                const key: string | number = (keyExtractor && keyExtractor(item, index)) ?? (index);
                return (
                  <ChildWrapper
                    key={key}
                  >
                    {renderItem && renderItem(item, index)}
                  </ChildWrapper>
                );
              })}
            </>
          )}
      </ListCard>
    </>
  );
};

const defaultProps = {
  data: undefined,
  fixedWidth: false,
  flexDirection: undefined,
  link: {
    children: undefined,
    color: Colors.PrimaryBlue,
    href: undefined,
    extraSmall: false,
    onClick: () => {},
  },
  heading: {
    headingText: undefined,
    large: false,
    medium: false,
    small: false,
    color: Colors.Gray,
    paddingBottom: '24px',
  },
};

CardList.defaultProps = defaultProps;

export default (withAppManager(CardList): React$ComponentType<
  React$Config<ManagedProps<Props>, typeof defaultProps>>);
